import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { TicketDetailView } from "@/components/admin/TicketDetailView";
import { safeUUID } from "@/lib/utils";

export default function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [ticket, setTicket] = useState<any>(null);
  const [admins, setAdmins] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ticketId) {
      // Validate UUID
      if (!safeUUID(ticketId)) {
        console.error(`Invalid ticket ID: "${ticketId}"`);
        toast({
          title: "Invalid Ticket ID",
          description: "The ticket ID is invalid.",
          variant: "destructive",
        });
        navigate('/admin/support/tickets');
        return;
      }
      loadTicket();
      loadAdmins();
    }
  }, [ticketId]);

  async function loadTicket() {
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select(`
          *,
          profiles:user_id (
            id,
            email,
            full_name
          )
        `)
        .eq('id', ticketId)
        .single();

      if (error) throw error;
      setTicket(data);
    } catch (error: any) {
      console.error('Error loading ticket:', error);
      toast({
        title: "Error",
        description: "Failed to load ticket",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function loadAdmins() {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select(`
          *,
          profiles:user_id (
            id,
            email,
            full_name
          )
        `)
        .eq('is_active', true);

      if (error) throw error;
      setAdmins(data || []);
    } catch (error: any) {
      console.error('Error loading admins:', error);
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">Loading ticket...</div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Ticket not found</p>
          <Button onClick={() => navigate('/admin/support/tickets')}>
            Back to Tickets
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Button
        variant="ghost"
        className="mb-4"
        onClick={() => navigate('/admin/support/tickets')}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Tickets
      </Button>

      <TicketDetailView
        ticket={ticket}
        open={true}
        onClose={() => navigate('/admin/support/tickets')}
        onUpdate={loadTicket}
        admins={admins}
      />
    </div>
  );
}